import { useEffect, useRef, useState } from 'react';

/** 카운트업 스킵 조건 — 테스트(jsdom 타이밍 비결정) 또는 reduced-motion 요청. */
function shouldSkip(): boolean {
  if (import.meta.env.MODE === 'test') return true;
  return typeof window !== 'undefined'
    && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

/**
 * 숫자 카운트업 훅 — 0(또는 직전 값)에서 target 까지 ease-out cubic 으로 증가.
 * 통계 KPI·게이지 % 등 등장 모션용. 스킵 조건에서는 즉시 최종값을 반환한다.
 */
export function useCountUp(target: number, durationMs = 800): number {
  const [value, setValue] = useState(() => (shouldSkip() ? target : 0));
  const fromRef = useRef(0);

  useEffect(() => {
    if (shouldSkip()) {
      setValue(target);
      fromRef.current = target;
      return;
    }
    const from = fromRef.current;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const p = Math.min((now - start) / durationMs, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(from + (target - from) * eased));
      if (p < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        fromRef.current = target;
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);

  return value;
}
